import store from '@/store';
import growthBook from 'services/growthBook';
import { isExchangeInProgress, resolveFinalPriceVariant } from './finalPriceExperiment';

/**
 * The same BOOK-168 assignment as `hooks/useFinalPriceExperiment`, read outside React: by class
 * components, thunks, the price sort and the trackers.
 *
 * It lives in its own module for the reason `priceWithoutFeesExperimentSort` does: reaching the
 * assignment without hooks needs the Redux store singleton, and the store's import graph pulls in
 * connected containers, so component suites that mock `react-redux` fail to load a module that
 * reaches it. The rule, the key and the tab latch are imported from
 * `utils/trips/finalPriceExperiment`, so the two readers cannot drift apart and always agree on
 * the tab's arm.
 *
 * Call it once per event or per list and pass the result down, never once per trip or per
 * comparison.
 *
 * @param {object} [gate] - What the surface is about to render.
 * @param {boolean} [gate.hasMarkup] - Whether the rendered pricing (or any trip in the list) has a
 *   companion above its total.
 * @param {boolean} [gate.hasDisplayTotal] - Whether it carries an INS-1288 display total.
 * @param {boolean} [gate.isExchange] - Whether the flow is an exchange. When omitted, it is read
 *   from the exchange slice (`isExchangeInProgress`).
 * @returns {boolean} Whether to use the final price.
 */
export const isFinalPriceVariantActive = ({ hasMarkup, hasDisplayTotal, isExchange } = {}) => {
  const { whitelabelConfig, exchange } = store.getState();
  const { env, features } = whitelabelConfig || {};

  return resolveFinalPriceVariant({
    features,
    brand: env?.brand,
    isExchange: isExchange ?? isExchangeInProgress(exchange),
    hasDisplayTotal,
    hasMarkup,
    growthBookInstance: growthBook.isInitialized() ? growthBook.getInstance() : undefined,
  });
};

export default isFinalPriceVariantActive;
